import { Box, Button, Heading, HStack, Text } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { GlobalCreditAccountDetailQuery } from "@src/__generated__/graphql";
import {
  CardHeader,
  CardIcon,
} from "@src/components/modules/credit-account/detail/common";
import { TransferGlobalCreditModal } from "@src/components/modules/credit-account/global-account-detail/components/transfer-credit-modal";
import { GlobalCreditAccountModal } from "@src/components/modules/credit-account/global-accounts-listing/components/modal";
import {
  type BodyColumnDef,
  Card,
  ColoredAmount,
  DataTable,
  Link,
  LoadingOverlay,
} from "@src/components/ui-kit";
import { currency, formatDate } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import mapToOptions from "@src/utils/map-to-options";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useMemo } from "react";
import { GlobalCreditAccountDetailStore } from "./store";

type CreditRow = NonNullable<
  GlobalCreditAccountDetailQuery["globalCreditAccount"]
>["credits"][0];

type DebitRow = NonNullable<
  GlobalCreditAccountDetailQuery["globalCreditAccount"]
>["debits"][0];

type Props = {
  id: string;
};
export const PageGlobalCreditAccountDetail = observer(
  function PageGlobalCreditAccountDetail({ id }: Props) {
    const appStore = useStore();
    const store = useLocalObservable(
      () => new GlobalCreditAccountDetailStore(id, appStore),
    );
    const creditColumns = useMemo<BodyColumnDef<CreditRow>[]>(
      () => [
        {
          id: "credit",
          meta: { label: t`Amount` },
          header: () => t`Amount`,
          cell: ({ row }) => (
            <ColoredAmount value={row.original.credit}>
              {currency.formatByWorkspace(row.original.credit)}
            </ColoredAmount>
          ),
        },
        {
          id: "origin",
          meta: { label: t`Origin` },
          header: () => t`Origin`,
          cell: ({ row }) =>
            row.original.originatesGlobalCreditAccount ? (
              <Link
                href={{
                  pathname: "/credit-accounts/detail",
                  query: { id: row.original.originatesGlobalCreditAccount.id },
                }}
              >
                {row.original.originatesGlobalCreditAccount.title}
              </Link>
            ) : row.original.expense ? (
              <Link
                href={{
                  pathname: "/expenses/edit",
                  query: { id: row.original.expense?.id },
                }}
              >
                {row.original.expense?.expense_no}
              </Link>
            ) : (
              <Text color="gray.400">-</Text>
            ),
        },
        {
          id: "date",
          meta: { label: t`Date` },
          header: () => t`Date`,
          cell: ({ row }) => formatDate(new Date(row.original.date)),
        },
      ],
      [],
    );
    const debitColumns = useMemo<BodyColumnDef<DebitRow>[]>(
      () => [
        {
          id: "project",
          meta: { label: t`Project` },
          header: () => t`Project`,
          cell: ({ row }) =>
            row.original.expenseBudgetItem?.project ? (
              <Link
                href={{
                  pathname: "/projects/detail",
                  query: {
                    id: row.original.expenseBudgetItem.project.id,
                  },
                }}
              >
                {
                  mapToOptions.projects([
                    row.original.expenseBudgetItem.project,
                  ])[0].label
                }
                &nbsp;/&nbsp;{row.original.expenseBudgetItem.title}
              </Link>
            ) : (
              <Text color="grey.400">-</Text>
            ),
        },
        {
          id: "credit",
          meta: { label: t`Amount` },
          header: () => t`Amount`,
          cell: ({ row }) => (
            <ColoredAmount value={-row.original.credit}>
              {currency.formatByWorkspace(row.original.credit)}
            </ColoredAmount>
          ),
        },
        {
          id: "performed_at",
          meta: { label: t`Date` },
          header: () => t`Date`,
          cell: ({ row }) => formatDate(new Date(row.original.performed_at)),
        },
      ],
      [],
    );

    return (
      <Box>
        <LoadingOverlay isLoading={store.isLoading}>
          {store.data && (
            <>
              <HStack justify="space-between">
                <Heading size="lg">{store.data.title}</Heading>
                <Button
                  onClick={() =>
                    store.editModalStore.modalState.onOpen({
                      globalCreditAccount: store.data ?? undefined,
                      onSubmit: store.handleEditSubmit,
                    })
                  }
                  variant="ghost"
                >
                  <Trans>Edit</Trans>
                </Button>
              </HStack>

              <Text my="4" color="grey.500">
                {store.data.note}
              </Text>

              <Card mb="6">
                <Card.Section>
                  <CardHeader
                    title={t`Remaining credit`}
                    icon={<CardIcon />}
                    additionalInfo={
                      <span className="font-semibold">
                        {currency.formatByWorkspace(
                          store.data.stats.remaining_credit,
                        )}
                      </span>
                    }
                    button={
                      <Button
                        onClick={
                          store.transferCreditModalStore
                            .getCreditModalDisclosure.onOpen
                        }
                      >
                        <Trans>Transfer credit</Trans>
                      </Button>
                    }
                  />

                  <TransferGlobalCreditModal
                    store={store.transferCreditModalStore}
                    onSubmit={() => store.fetch(id)}
                    ids={{
                      fromId: store.data.creditAccount.id,
                    }}
                    modalTitle={t`Transfer credit`}
                    ignoreIds={[id]}
                    enableSourceSelect
                  />
                </Card.Section>
              </Card>

              <Card mb="6">
                <Card.Section>
                  <Heading mb="4" size="md">
                    {t`Credits`}
                  </Heading>
                </Card.Section>
                <DataTable>
                  <DataTable.Body
                    tableKey="global-credit-account-credits"
                    data={store.data.credits}
                    columns={creditColumns}
                    enableColumnVisibilityControl={false}
                  />
                </DataTable>
              </Card>

              <Card>
                <Card.Section>
                  <Heading mb="4" size="md">
                    {t`Debits`}
                  </Heading>
                </Card.Section>
                <DataTable>
                  <DataTable.Body
                    tableKey="global-credit-account-debits"
                    data={store.data.debits}
                    columns={debitColumns}
                    enableColumnVisibilityControl={false}
                  />
                </DataTable>
              </Card>
            </>
          )}
        </LoadingOverlay>

        <GlobalCreditAccountModal
          store={store.editModalStore}
          isLoading={store.isEditing}
        />
      </Box>
    );
  },
);
